import {
  ErrorDisplay,
  FormCheckbox,
  FormDatePicker,
  FormHub,
  StyledDialog,
  SubmitButton,
  useFormEvents,
} from '@lib/common';
import { FormStatus } from '@lib/state';
import { Chip, DialogContent, Grid, TextField } from '@material-ui/core';
import { InvoiceStatusForms } from 'app/state';
import React, { useCallback, useEffect, useState } from 'react';

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmit: (
    recipients: Array<string> | undefined,
    dateSent: string,
    replaceRecipients: boolean
  ) => void;
}

interface FormFields {
  dateSent: string;
  replaceRecipients: boolean;
}

export function SetInvoiceSentForm({ open, onClose, onSubmit }: Props) {
  const [recipients, setRecipients] = useState<Array<string>>([]);
  const [inputValue, setInputValue] = useState('');
  const [{ status, error: formError }, reset] = useFormEvents(
    InvoiceStatusForms.MarkInvoicesSentForm
  );

  useEffect(() => {
    if (status === FormStatus.Success) {
      setRecipients([]);
      setInputValue('');
      reset();
      onClose();
    }
  }, [status, reset, setRecipients, setInputValue, onClose]);

  const addRecipient = useCallback(
    (recipient: string) => {
      setRecipients(prev => [...prev, recipient]);
    },
    [setRecipients]
  );

  const removeRecipient = useCallback(
    (recipient: string) => {
      setRecipients(prev => prev.filter(r => r !== recipient));
    },
    [setRecipients]
  );

  const handleSubmit = useCallback(
    ({ dateSent, replaceRecipients }: FormFields) => {
      if (recipients.length !== 0) {
        if (inputValue.trim().length !== 0) {
          addRecipient(inputValue.trim());
        }
        onSubmit(recipients, dateSent, replaceRecipients);
      } else if (inputValue.trim().length !== 0) {
        onSubmit([inputValue.trim()], dateSent, replaceRecipients);
      } else {
        onSubmit(undefined, dateSent, replaceRecipients);
      }
    },
    [recipients, inputValue, onSubmit, addRecipient]
  );

  const handleKeyDown = useCallback(
    event => {
      if (event.key === 'Enter') {
        if (!recipients.includes(inputValue.trim())) {
          addRecipient(inputValue.trim());
        }
        setInputValue('');
      }

      if (event.key === 'Backspace' && inputValue === '' && recipients.length > 0) {
        removeRecipient(recipients[recipients.length - 1]!);
      }
    },
    [inputValue, recipients, addRecipient, removeRecipient]
  );

  return (
    <StyledDialog title="Add Invoice Recipients" open={open} onClose={onClose}>
      <DialogContent>
        <TextField
          label="Add Recipients"
          fullWidth
          value={inputValue}
          onChange={e => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          InputProps={{
            startAdornment: recipients.map(r => (
              <Chip
                key={r}
                label={r}
                onDelete={() => removeRecipient(r)}
                style={{ margin: '2px' }}
              />
            )),
          }}
        />
        <FormHub<FormFields> onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormDatePicker
                name="dateSent"
                label="Date Sent"
                fullWidth
                validationOptions={{ required: true }}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <FormCheckbox
                name="replaceRecipients"
                label="Replace current"
                fullWidth
                defaultChecked={false}
              />
            </Grid>
            <Grid item xs={12}>
              <ErrorDisplay error={formError} />
              <SubmitButton
                variant="contained"
                color="secondary"
                fullWidth
                pending={status === FormStatus.Pending}
                disabled={status === FormStatus.Pending}
              >
                Submit
              </SubmitButton>
            </Grid>
          </Grid>
        </FormHub>
      </DialogContent>
    </StyledDialog>
  );
}
